import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getAuth, signOut } from 'firebase/auth'

import { FooterContainer } from 'components/FooterContainer'
import { Modal } from 'components/Modal'
import { Spinner } from 'components/Spinner'

import { setErrorAction } from 'root-redux/actions/common'
import {
  UNSUBSCRIBE,
  getUserSubscriptionsInfoUnifiedAction,
  withdrawSubscriptionAction,
} from 'root-redux/actions/user'
import {
  selectActionList,
  selectAppName,
  selectSource,
} from 'root-redux/selects/common'
import {
  selectUUID,
  selectUserContactEmail,
  selectUserSubscriptionInfo,
} from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { useAmplitudeInitialization } from 'hooks/useAmplitudeInitialization'
import { useRetentionFlowApp } from 'hooks/useRetentionFlowApp'

import { getLoginMethodFromLocalStorage } from 'helpers/getLoginMethodFromLocalStorage'

import {
  setIsModalShownAction,
  setIsWithdrawalModal,
} from 'modules/contactForm/redux'
import { Account } from 'modules/unsubscribe/components/Account'
import { ContactSupport } from 'modules/unsubscribe/components/ContactSupport'
import { DetailsTab } from 'modules/unsubscribe/components/DetailsTabs'
import { RetentionPageContainer } from 'modules/unsubscribe/components/RetentionPageContainer'
import {
  DetailedSubscriptions,
  GiftedSubscription,
  LifetimeSubscription,
  Subscriptions,
} from 'modules/unsubscribe/components/Subscriptions'

import { ICustomRouterState } from 'models/common.model'

import { eventLogger } from 'services/eventLogger.service'

import { goTo } from 'browser-history'
import { SubscriptionTitle } from 'common-styles'
import { PageId } from 'page-constants'
import {
  AppName,
  HELP_CENTER_LINK_APPS,
  SubscriptionDetailsTab,
} from 'root-constants'

import { StyledSubscriptionDetails as S } from './SubscriptionDetails.styles'
import { WithdrawalModal } from './components'
import { APP_SUBSCRIPTIONS_TABS } from './constants'

const MAX_AVAILABLE_MONTHS_SUBSCRIPTION_PLAN = 36

export const SubscriptionDetails: React.FC = () => {
  const { t } = useTranslation()
  const { state, search } = useLocation()
  const dispatch: TAppDispatch = useDispatch()

  const [selectedTab, setSelectedTab] = useState(
    (state as ICustomRouterState)?.defaultTab ?? SubscriptionDetailsTab.ACCOUNT,
  )
  const [withdrawalTargetId, setWithdrawalTargetId] = useState<string | null>(
    null,
  )
  const [withdrawalContract, setWithdrawalContract] = useState('')

  const userSubscriptionInfo = useSelector(selectUserSubscriptionInfo)
  const fetchingActionsList = useSelector(selectActionList)
  const source = useSelector(selectSource)
  const email = useSelector(selectUserContactEmail)
  const userId = useSelector(selectUUID)
  const appName = useSelector(selectAppName)
  const isRetentionFlowApp = useRetentionFlowApp()

  const isUnsubscribeInProgress = useMemo(
    () => fetchingActionsList.includes(UNSUBSCRIBE),
    [fetchingActionsList],
  )

  const signOutFirebase = useCallback(() => signOut(getAuth()), [])

  useEffect(() => {
    if (!userId || !email) return

    eventLogger.logSubscriptionDetailsShow({
      userId,
      source,
      email,
      method: getLoginMethodFromLocalStorage(),
    })
  }, [email, source, userId])

  const handleCloseModal = useCallback(() => {
    dispatch(setErrorAction(''))

    dispatch(getUserSubscriptionsInfoUnifiedAction(signOutFirebase))

    dispatch(setIsModalShownAction(false))
    dispatch(setIsWithdrawalModal(false))
  }, [dispatch, signOutFirebase])

  const onConfirmWithdrawal = useCallback(
    (data) => {
      if (!withdrawalTargetId) return

      dispatch(withdrawSubscriptionAction(data, withdrawalTargetId))
      goTo(
        { pathname: PageId.CANCELED_SUBSCRIPTION, search },
        { isWithdrawal: true },
      )
    },
    [dispatch, search, withdrawalTargetId],
  )

  const handleRequestWithdrawal = useCallback(
    (subscriptionId: string, contract: string) => {
      setWithdrawalTargetId(subscriptionId)
      setWithdrawalContract(contract)
    },
    [],
  )

  const renderSubscriptionsTab = () => {
    if (userSubscriptionInfo?.isGift) return <GiftedSubscription />
    const AppSubscriptionsTab = APP_SUBSCRIPTIONS_TABS[appName as AppName]
    if (AppSubscriptionsTab) {
      return (
        <AppSubscriptionsTab onRequestWithdrawal={handleRequestWithdrawal} />
      )
    }
    if (
      appName === AppName.LUVLY &&
      userSubscriptionInfo?.intervalCount ===
        MAX_AVAILABLE_MONTHS_SUBSCRIPTION_PLAN
    ) {
      return <LifetimeSubscription />
    }
    if (isRetentionFlowApp) {
      return (
        <DetailedSubscriptions onRequestWithdrawal={handleRequestWithdrawal} />
      )
    }
    return <Subscriptions onRequestWithdrawal={handleRequestWithdrawal} />
  }

  useAmplitudeInitialization()

  return !email || isUnsubscribeInProgress ? (
    <Spinner />
  ) : (
    <RetentionPageContainer>
      <SubscriptionTitle>{t('subscription.title')}</SubscriptionTitle>
      <DetailsTab activeTab={selectedTab} handleChange={setSelectedTab} />
      <S.Container>
        {selectedTab === SubscriptionDetailsTab.ACCOUNT && <Account />}
        {selectedTab === SubscriptionDetailsTab.SUBSCRIPTIONS &&
          renderSubscriptionsTab()}
        <Modal onClose={handleCloseModal} hasContactSupport />
      </S.Container>
      <FooterContainer>
        <ContactSupport
          showHelpCenterLink={HELP_CENTER_LINK_APPS.includes(appName)}
        />
      </FooterContainer>
      <WithdrawalModal
        isShown={!!withdrawalTargetId}
        contract={withdrawalContract}
        onClose={() => setWithdrawalTargetId(null)}
        onConfirm={onConfirmWithdrawal}
      />
    </RetentionPageContainer>
  )
}
